import {
    useCallback,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
    type FocusEvent,
} from "react";
import type { BoardColumnDef, BoardLaneDef } from "./Board.types";
import { hasFocusLeft, PLACEMENT_KEY_ATTRIBUTE } from "./boardAttributes";
import { getCellEntries, type BoardCells } from "./boardCells";
import { getLaneIds } from "./boardCollapse";
import { locateBoardEntry } from "./boardMove";

interface UseBoardFocusedKeyArgs<TItem> {
    cells: BoardCells<TItem>;
    columns: readonly BoardColumnDef[];
    lanes?: readonly BoardLaneDef[];
    /** A card is dragged or settling: its preview key isn't in `cells`. */
    isDragActive: boolean;
    /** Read on mount: the item whose first card starts as the focused one. */
    initialFocusedItemId?: string;
}

interface FocusedCard {
    key: string;
    itemId: string;
}

/** The item's first card in board order (lane by lane, column by column). */
const findItemCard = <TItem>({
    cells,
    columns,
    lanes,
    itemId,
}: Pick<UseBoardFocusedKeyArgs<TItem>, "cells" | "columns" | "lanes"> & {
    itemId: string;
}) => {
    for (const laneId of getLaneIds(lanes)) {
        for (const column of columns) {
            const entry = getCellEntries(cells, {
                columnId: column.id,
                laneId,
            }).find((cellEntry) => cellEntry.itemId === itemId);
            if (entry !== undefined) {
                return entry;
            }
        }
    }
    return null;
};

/**
 * Tracks which card has focus inside the board root the handlers go on, so
 * windowed lists keep it mounted while it scrolls out of view: unmounting
 * it would drop focus to the page. Focus in a popup a card opened (a
 * portal, outside the root) still counts as that card's. When an edit moves
 * the item to another cell, the key follows it to its card there. The key
 * clears once focus leaves the board or the item leaves `cells` (deleted or
 * filtered out), so a stale key never pins a card or lane, nor comes back
 * with the card; `clearFocusedKey` clears it when focus can't get back to
 * the card. While a drag is on, a card that's only in the drag preview
 * keeps its key (it reads as null), so a keyboard-dragged card is the
 * focused one again once the move lands in `cells`. On mount the key starts
 * at `initialFocusedItemId`'s card while focus is on the page, so
 * `useBoardFocusRestore` focuses it once it mounts; with focus already on
 * another element it starts empty, so the card can't pull focus back later.
 */
export const useBoardFocusedKey = <TItem>({
    cells,
    columns,
    lanes,
    isDragActive,
    initialFocusedItemId,
}: UseBoardFocusedKeyArgs<TItem>) => {
    const [focused, setFocused] = useState<FocusedCard | null>(() => {
        const entry =
            initialFocusedItemId === undefined
                ? null
                : findItemCard({
                      cells,
                      columns,
                      lanes,
                      itemId: initialFocusedItemId,
                  });
        return entry === null
            ? null
            : { key: entry.placementKey, itemId: entry.itemId };
    });
    const seedRef = useRef(focused);
    useLayoutEffect(() => {
        const seed = seedRef.current;
        const active = document.activeElement;
        if (seed !== null && active !== null && active !== document.body) {
            setFocused((current) => (current === seed ? null : current));
        }
    }, []);
    // The last element focused in the board or its popups, and its card.
    const lastFocusRef = useRef<{
        event: Event;
        element: EventTarget;
        card: FocusedCard | null;
    } | null>(null);
    const location = useMemo(
        () => (focused === null ? null : locateBoardEntry(cells, focused.key)),
        [cells, focused],
    );
    if (focused !== null && location === null && !isDragActive) {
        const moved = findItemCard({
            cells,
            columns,
            lanes,
            itemId: focused.itemId,
        });
        setFocused(
            moved === null ? null : { ...focused, key: moved.placementKey },
        );
    }
    const getFocusedCard = useCallback(
        (target: Element): FocusedCard | null => {
            const key =
                target
                    .closest(`[${PLACEMENT_KEY_ATTRIBUTE}]`)
                    ?.getAttribute(PLACEMENT_KEY_ATTRIBUTE) ?? null;
            const entry = key === null ? null : locateBoardEntry(cells, key);
            return entry === null
                ? null
                : { key: entry.entry.placementKey, itemId: entry.entry.itemId };
        },
        [cells],
    );
    const onFocus = useCallback(
        (event: FocusEvent<HTMLElement>) => {
            // React delivers focus inside a Base UI popup twice (its portal
            // sits in a sibling portal on the body); the second would no
            // longer find the element focus came from.
            if (lastFocusRef.current?.event === event.nativeEvent) {
                return;
            }
            const root = event.currentTarget;
            // A popup is a card's only when focus came straight from that
            // card or from within its popup, not from the page or from
            // nowhere (a click that didn't focus its trigger).
            const getPopupCard = () => {
                const from = event.relatedTarget;
                if (from instanceof Element && root.contains(from)) {
                    return getFocusedCard(from);
                }
                const last = lastFocusRef.current;
                return last !== null && from === last.element
                    ? last.card
                    : null;
            };
            const card = root.contains(event.target)
                ? getFocusedCard(event.target)
                : getPopupCard();
            lastFocusRef.current = {
                event: event.nativeEvent,
                element: event.target,
                card,
            };
            setFocused(card);
        },
        [getFocusedCard],
    );
    const onBlur = useCallback((event: FocusEvent<HTMLElement>) => {
        if (!hasFocusLeft(event)) {
            return;
        }
        const destination = event.relatedTarget;
        if (destination === null) {
            setFocused(null);
            return;
        }
        // Focus going to an element outside the root may be going to a
        // card's popup, which `onFocus` sees first as this move's focusin.
        // Clearing now would render the board without its focused card in
        // between, when a pointer press lets React render after the blur.
        event.currentTarget.ownerDocument.addEventListener(
            "focusin",
            (focusIn) => {
                if (lastFocusRef.current?.event !== focusIn) {
                    setFocused(null);
                }
            },
            { once: true },
        );
    }, []);
    const clearFocusedKey = useCallback(() => setFocused(null), []);
    return {
        focusedKey: location === null ? null : (focused?.key ?? null),
        focusedLaneId: location?.entry.placement.laneId ?? null,
        focusHandlers: { onFocus, onBlur },
        clearFocusedKey,
    };
};
